import React, { createContext, useContext, useState, useEffect } from 'react';
import { 
  getAllPages as getPages, 
  getPage, 
  createPage as addPage, 
  updatePage, 
  deletePage,
  updatePageStatus,
  permanentlyDeletePage,
  restorePage
} from '../services/pageApi';
import { toast } from 'react-hot-toast';

const PageContext = createContext();

export const usePages = () => {
  const context = useContext(PageContext);
  if (!context) {
    throw new Error('usePages must be used within a PageProvider');
  }
  return context;
};

export const PageProvider = ({ children }) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Tüm sayfaları getir
  const fetchPages = async () => {
    try {
      console.log('Sayfalar getiriliyor...');
      setLoading(true);
      const data = await getPages();
      console.log('Getirilen sayfalar:', data);
      setPages(data);
      setError(null);
    } catch (error) {
      console.error('Sayfalar yüklenirken hata:', error);
      setError(error.message);
      toast.error('Sayfalar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  // Sayfa ekle
  const addPage = async (pageData) => {
    try {
      setLoading(true);
      const newPage = await addPage(pageData);
      setPages(prev => [newPage, ...prev]);
      toast.success('Sayfa başarıyla eklendi');
      return newPage;
    } catch (error) {
      console.error('Sayfa eklenirken hata:', error);
      toast.error(error.message || 'Sayfa eklenirken bir hata oluştu');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sayfa güncelle
  const editPage = async (pageId, pageData) => {
    try {
      setLoading(true);
      const updatedPage = await updatePage(pageId, pageData);
      setPages(prev => prev.map(page => 
        page.id === pageId ? { ...page, ...updatedPage } : page
      ));
      toast.success('Sayfa başarıyla güncellendi');
      return updatedPage;
    } catch (error) {
      console.error('Sayfa güncellenirken hata:', error);
      toast.error(error.message || 'Sayfa güncellenirken bir hata oluştu');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sayfa durumunu güncelle
  const changePageStatus = async (pageId, status) => {
    try {
      setLoading(true);
      await updatePageStatus(pageId, status);
      setPages(prev => prev.map(page =>
        page.id === pageId ? { ...page, status } : page
      ));
      toast.success('Sayfa durumu güncellendi');
    } catch (error) {
      console.error('Sayfa durumu güncellenirken hata:', error);
      toast.error('Sayfa durumu güncellenirken bir hata oluştu');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sayfayı sil (yumuşak silme)
  const removePage = async (pageId) => {
    try {
      setLoading(true);
      await deletePage(pageId);
      setPages(prev => prev.map(page =>
        page.id === pageId ? { ...page, status: 'deleted' } : page
      ));
      toast.success('Sayfa silindi');
    } catch (error) {
      console.error('Sayfa silinirken hata:', error);
      toast.error('Sayfa silinirken bir hata oluştu');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Sayfayı kalıcı olarak sil
  const removePagePermanently = async (pageId) => {
    try {
      setLoading(true);
      await permanentlyDeletePage(pageId);
      setPages(prev => prev.filter(page => page.id !== pageId));
      toast.success('Sayfa kalıcı olarak silindi');
    } catch (error) {
      console.error('Sayfa kalıcı olarak silinirken hata:', error);
      toast.error('Sayfa kalıcı olarak silinirken bir hata oluştu');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Silinen sayfayı geri yükle
  const restoreDeletedPage = async (pageId) => {
    try {
      setLoading(true);
      await restorePage(pageId);
      setPages(prev => prev.map(page =>
        page.id === pageId ? { ...page, status: 'draft', deletedAt: null } : page
      ));
      toast.success('Sayfa geri yüklendi');
    } catch (error) {
      console.error('Sayfa geri yüklenirken hata:', error);
      toast.error('Sayfa geri yüklenirken bir hata oluştu');
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('PageProvider yüklendi');
    fetchPages();
  }, []);

  const value = {
    pages,
    loading,
    error,
    fetchPages,
    addPage,
    editPage,
    changePageStatus,
    removePage,
    removePagePermanently,
    restoreDeletedPage
  };

  return (
    <PageContext.Provider value={value}>
      {children}
    </PageContext.Provider>
  );
};

export default PageContext; 